import { Dialog, DialogContent, DialogTrigger, DialogClose } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import type { AnswerControllerProps } from './AnswerController';
import { useState } from 'react';
import { useReviews } from './ReviewsProvider';
import { motion, AnimatePresence } from 'framer-motion';
import { type ListTemplateProps, EmptyList, TemplateItem } from './ListTemplate';
import {
  LayoutTemplateContainer,
  LayoutTemplateTitle,
  LayoutTemplateContent,
} from '../../_layouts/LayoutsEditTemplate';
import { SelectedTemplate } from './SelectedTemplate';

interface SelectTemplateProps extends Pick<AnswerControllerProps, 'setComment'> {}

const SelectTemplate = ({ setComment }: SelectTemplateProps) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'list' | 'confirm'>('list');
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  const { templateList } = useReviews();

  // 템플릿 선택 시 상세 화면으로
  const handleSelect = (idx: number) => {
    setSelectedIdx(idx);
    setMode('confirm');
  };

  // 리스트로 돌아가기 (상세에서)
  const handleBack = () => {
    setMode('list');
    setSelectedIdx(null);
  };

  // 답변 선택 시 입력란에 반영하고 닫기
  const handleConfirm = () => {
    if (selectedIdx !== null && templateList[selectedIdx]) {
      setComment?.(templateList[selectedIdx].Content);
      setOpen(false);
      setMode('list');
      setSelectedIdx(null);
    }
  };

  // 다이얼로그 닫힐 때 상태 초기화
  const handleDialogChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setMode('list');
      setSelectedIdx(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="xsmall">
          템플릿에서 선택
        </Button>
      </DialogTrigger>
      <DialogContent
        noCloseBtn
        className="h-[458px] max-w-[620px] overflow-x-hidden rounded-xl bg-white p-[30px]"
      >
        <AnimatePresence initial={false} mode="popLayout">
          <div className="h-full w-full overflow-x-hidden">
            {mode === 'list' && (
              <motion.div
                key="list"
                initial={{ x: -420, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="h-full w-full"
              >
                <ListTemplate templateList={templateList} handleSelect={handleSelect} />
              </motion.div>
            )}
            {mode === 'confirm' && selectedIdx !== null && (
              <motion.div
                key="confirm"
                initial={{ x: 420, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="h-full w-full"
              >
                <SelectedTemplate
                  title={templateList[selectedIdx].Title}
                  content={templateList[selectedIdx].Content}
                  onBack={handleBack}
                  onSelect={handleConfirm}
                />
              </motion.div>
            )}
          </div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default SelectTemplate;

export const ListTemplate = ({
  templateList,
  handleSelect,
}: {
  templateList: ListTemplateProps['templateList'];
  handleSelect: (idx: number) => void;
}) => {
  return (
    <LayoutTemplateContainer>
      <LayoutTemplateTitle>
        <div className="">템플릿 선택</div>
      </LayoutTemplateTitle>
      <LayoutTemplateContent>
        {templateList.length === 0 ? (
          <EmptyList />
        ) : (
          templateList.map((tpl, idx) => (
            <TemplateItem tpl={tpl} onClick={() => handleSelect(idx)} />
          ))
        )}
      </LayoutTemplateContent>
      <ListTemplateController />
    </LayoutTemplateContainer>
  );
};

const ListTemplateController = () => {
  return (
    <DialogClose asChild>
      <Button variant="secondary" className="h-[44px]   Body5M14">
        닫기
      </Button>
    </DialogClose>
  );
};
